import { useEffect, useRef, type RefObject } from "react";
import { useReducedMotion } from "@/lib/motion";

// Porta fiel da rede de nós do projeto importado (user_specs/login-refactor).
// Helpers puros são exportados p/ teste; a parte imperativa (canvas 2D) é fina e
// guardada contra ctx nulo (jsdom) e reduced-motion (desenha 1 quadro estático).

export function nodeCountForArea(area: number): number {
  return Math.max(28, Math.min(60, Math.round(area / 26000)));
}

export interface NetworkPalette {
  link0: (o: number) => string;
  link1: (o: number) => string;
  nodeWarm: string;
  nodeCool: string;
  glowWarm: string;
  glowCool: string;
}

export function networkPalette(isDark: boolean): NetworkPalette {
  // Dark = paleta do design. Light = mesmos matizes, mais escuros p/ contraste em fundo claro.
  if (isDark) {
    return {
      link0: (o) => `oklch(62% 0.17 285 / ${o})`,
      link1: (o) => `oklch(72% 0.175 55 / ${o * 0.8})`,
      nodeWarm: "oklch(75% 0.17 55)",
      nodeCool: "oklch(72% 0.15 285)",
      glowWarm: "oklch(72% 0.175 55 / .6)",
      glowCool: "oklch(62% 0.17 285 / .6)",
    };
  }
  return {
    link0: (o) => `oklch(50% 0.18 285 / ${o})`,
    link1: (o) => `oklch(60% 0.17 55 / ${o * 0.8})`,
    nodeWarm: "oklch(60% 0.17 55)",
    nodeCool: "oklch(52% 0.18 285)",
    glowWarm: "oklch(60% 0.17 55 / .5)",
    glowCool: "oklch(52% 0.18 285 / .5)",
  };
}

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  c: number;
}
const LINK = 165;

export function useNetworkCanvas(isDark: boolean): RefObject<HTMLCanvasElement> {
  const ref = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    let ctx: CanvasRenderingContext2D | null;
    try {
      ctx = cv.getContext("2d");
    } catch {
      return; // jsdom / ambiente sem suporte a canvas (getContext lança "Not implemented")
    }
    if (!ctx) return;
    const pal = networkPalette(isDark);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let W = 0;
    let H = 0;
    let nodes: Node[] = [];
    let raf = 0;
    let rt: ReturnType<typeof setTimeout> | undefined;

    function build(): void {
      nodes = Array.from({ length: nodeCountForArea(W * H) }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: Math.random() * 2 + 1.3,
        c: Math.random(),
      }));
    }
    function size(): void {
      const rect = cv!.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      cv!.width = W * dpr;
      cv!.height = H * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    }
    function frame(step: boolean): void {
      ctx!.clearRect(0, 0, W, H);
      if (step) {
        for (const a of nodes) {
          a.x += a.vx;
          a.y += a.vy;
          if (a.x < 0 || a.x > W) a.vx *= -1;
          if (a.y < 0 || a.y > H) a.vy *= -1;
        }
      }
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]!;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]!;
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < LINK) {
            const o = (1 - d / LINK) * 0.5;
            const g = ctx!.createLinearGradient(a.x, a.y, b.x, b.y);
            g.addColorStop(0, pal.link0(o));
            g.addColorStop(1, pal.link1(o));
            ctx!.strokeStyle = g;
            ctx!.lineWidth = 0.7;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.stroke();
          }
        }
      }
      for (const a of nodes) {
        ctx!.beginPath();
        ctx!.arc(a.x, a.y, a.r, 0, 7);
        ctx!.fillStyle = a.c > 0.7 ? pal.nodeWarm : pal.nodeCool;
        ctx!.shadowBlur = 8;
        ctx!.shadowColor = a.c > 0.7 ? pal.glowWarm : pal.glowCool;
        ctx!.fill();
        ctx!.shadowBlur = 0;
      }
    }
    function loop(): void {
      frame(true);
      raf = requestAnimationFrame(loop);
    }

    size();
    if (reduced) frame(false);
    else loop();

    const onResize = (): void => {
      clearTimeout(rt);
      rt = setTimeout(() => {
        size();
        if (reduced) frame(false);
      }, 120);
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(rt);
      window.removeEventListener("resize", onResize);
    };
  }, [isDark, reduced]);

  return ref;
}
